import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { SignaturePad } from '@/components/SignaturePad';
import { 
  Loader2, 
  FileText, 
  CheckCircle, 
  XCircle,
  PenTool,
  User,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Error states
function InvalidRequest({ message, lang }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <XCircle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle>{lang === 'ru' ? 'Ошибка' : 'Error'}</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

// Success state
function SignatureSuccess({ lang }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle>{lang === 'ru' ? 'Документ подписан!' : 'Document Signed!'}</CardTitle>
          <CardDescription>
            {lang === 'ru' 
              ? 'Спасибо! Ваша подпись сохранена. Вы можете закрыть эту страницу.'
              : 'Thank you! Your signature has been saved. You can close this page.'}
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

export default function SignDocumentPage() {
  const [searchParams] = useSearchParams();
  const { lang } = useLanguage();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [signerName, setSignerName] = useState('');

  const loadDocument = useCallback(async () => {
    if (!token) {
      setError(lang === 'ru' ? 'Ссылка недействительна' : 'Invalid link');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/signature/verify?token=${token}`);
      setDocumentData(res.data);
      if (res.data.client?.name) {
        setSignerName(res.data.client.name);
      }
    } catch (err) {
      console.error('Verify error:', err);
      if (err.response?.status === 410) {
        setError(lang === 'ru' ? 'Срок действия ссылки истёк' : 'This link has expired');
      } else if (err.response?.status === 404) {
        setError(lang === 'ru' ? 'Документ не найден или уже подписан' : 'Document not found or already signed');
      } else {
        setError(lang === 'ru' ? 'Ошибка загрузки документа' : 'Failed to load document');
      }
    } finally {
      setLoading(false);
    }
  }, [token, lang]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  const handleSign = async () => {
    if (!signatureData) {
      alert(lang === 'ru' ? 'Пожалуйста, поставьте подпись' : 'Please add your signature');
      return;
    }
    if (!signerName.trim()) {
      alert(lang === 'ru' ? 'Пожалуйста, введите ваше имя' : 'Please enter your name');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/signature/sign?token=${token}`, {
        signature_data: signatureData,
        signer_name: signerName.trim()
      });
      setSuccess(true);
    } catch (err) {
      console.error('Sign error:', err);
      alert(lang === 'ru' ? 'Ошибка подписания' : 'Failed to sign document');
    } finally {
      setSigning(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  // Error
  if (error) {
    return <InvalidRequest message={error} lang={lang} />;
  }

  // Success
  if (success) {
    return <SignatureSuccess lang={lang} />;
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-accent" />
              </div>
              <div>
                <CardTitle>{documentData?.document?.title || 'Document'}</CardTitle>
                <CardDescription>
                  {lang === 'ru' ? 'Пожалуйста, прочитайте и подпишите документ' : 'Please review and sign the document'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Document Content */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {lang === 'ru' ? 'Содержание документа' : 'Document Content'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-sm dark:prose-invert max-w-none p-4 bg-muted/30 rounded-lg border min-h-[200px] max-h-[400px] overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: documentData?.document?.content || '<p>No content</p>' }}
            />
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PenTool className="h-5 w-5" />
              {lang === 'ru' ? 'Ваша подпись' : 'Your Signature'}
            </CardTitle>
            <CardDescription>
              {lang === 'ru' 
                ? 'Нарисуйте подпись пальцем или мышкой'
                : 'Draw your signature with finger or mouse'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Signer Name */}
            <div className="space-y-2">
              <Label htmlFor="signer-name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {lang === 'ru' ? 'Ваше имя (печатными буквами)' : 'Your Name (print)'}
              </Label>
              <Input
                id="signer-name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder={lang === 'ru' ? 'Иван Иванов' : 'John Smith'}
                data-testid="signer-name-input"
              />
            </div>

            <Separator />

            {/* Signature Pad */}
            <div>
              <Label className="mb-2 block">
                {lang === 'ru' ? 'Подпись' : 'Signature'}
              </Label>
              <SignaturePad 
                onSignatureChange={setSignatureData}
                width={500}
                height={200}
              />
            </div>

            {/* Legal Notice */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {lang === 'ru' 
                  ? 'Нажимая "Подписать", вы подтверждаете, что прочитали документ и согласны с его условиями.'
                  : 'By clicking "Sign", you confirm that you have read the document and agree to its terms.'}
              </p>
            </div>

            {/* Sign Button */}
            <Button 
              className="w-full h-12 text-lg"
              onClick={handleSign}
              disabled={signing || !signatureData || !signerName.trim()}
              data-testid="sign-document-btn"
            >
              {signing ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 mr-2" />
              )}
              {lang === 'ru' ? 'Подписать документ' : 'Sign Document'}
            </Button>
          </CardContent>
        </Card>

        {/* Order Info */}
        {documentData?.order?.title && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                {lang === 'ru' ? 'Заказ' : 'Order'}: <span className="font-medium text-foreground">{documentData.order.title}</span>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
